import React, { useState } from "react";
import axios from "axios";
import { useTheme } from "next-themes";
import { Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import SectionHeader from "@/components/brams/SectionHeader";
import Reveal from "@/components/brams/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SnapIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2c3.3 0 5.6 2.4 5.6 5.6 0 .7-.1 1.7-.2 2.6.4.2.9.4 1.5.4.4 0 .9-.1 1.3-.4.2-.1.6.1.6.4 0 .3-.4 1-2.1 1.5-.3.1-.5.2-.5.5 0 1.3 3.5 2.5 4.4 2.7.3.1.5.4.4.7-.1.3-.6 1-3.4 1.5-.1 0-.1.2-.1.3 0 .3-.5 1.1-1.6 1.1-.6 0-1.2-.2-1.9-.2-1.4 0-2.1.4-3.1 1.2-.8.7-1.7 1.5-3 1.5s-2.2-.8-3-1.5c-1-.8-1.7-1.2-3.1-1.2-.7 0-1.3.2-1.9.2-1.1 0-1.6-.8-1.6-1.1 0-.1 0-.3-.1-.3C2.4 17.5 1.9 16.8 1.8 16.5c-.1-.3.1-.6.4-.7.9-.2 4.4-1.4 4.4-2.7 0-.3-.2-.4-.5-.5C4.4 12 4 11.4 4 11c0-.3.4-.5.6-.4.4.3.9.4 1.3.4.6 0 1.1-.2 1.5-.4-.1-.9-.2-1.9-.2-2.6C7.2 4.4 9.4 2 12 2z" />
  </svg>
);

export default function Contact() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.email_sent) {
        toast.success("Message envoyé. Je reviens vers vous très vite.");
      } else {
        toast.success("Message bien reçu. Je vous réponds rapidement.");
      }
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = cn(
    "w-full bg-transparent py-4 px-0 border-b text-base font-body font-light placeholder:opacity-50 focus:outline-none transition-colors duration-300",
    isDark
      ? "border-white/15 focus:border-white text-white placeholder:text-white/40"
      : "border-black/15 focus:border-black text-black placeholder:text-black/40"
  );

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <SectionHeader
            eyebrow="Contactez-moi"
            title={
              <>
                Une marque à <em className="not-italic opacity-60">construire</em> ?
                <br /> Parlons-en.
              </>
            }
            subtitle="J&apos;accepte un nombre limité de projets par trimestre. Parlez-moi du vôtre — je vous réponds personnellement sous 48 heures."
          />

          <Reveal delay={300}>
            <div className="mt-12 space-y-5">
              <a
                href="mailto:bramsgraphic7@gmail.com"
                data-testid="contact-email-link"
                className={cn(
                  "group flex items-center gap-4 transition-all duration-300",
                  isDark ? "hover:text-white text-white/85" : "hover:text-black text-black/85"
                )}
              >
                <span
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110",
                    isDark ? "bg-white/5 border border-white/10" : "bg-black/5 border border-black/10"
                  )}
                >
                  <Mail size={16} />
                </span>
                <div>
                  <div className={cn("text-[10px] uppercase tracking-[0.3em]", isDark ? "text-white/55" : "text-black/55")}>
                    Email
                  </div>
                  <div className="font-display font-medium tracking-tight">
                    bramsgraphic7@gmail.com
                  </div>
                </div>
              </a>

              <a
                href="https://snapchat.com/add/bramsgraphic78"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-snapchat-link"
                className={cn(
                  "group flex items-center gap-4 transition-all duration-300",
                  isDark ? "hover:text-white text-white/85" : "hover:text-black text-black/85"
                )}
              >
                <span
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110",
                    isDark ? "bg-white/5 border border-white/10" : "bg-black/5 border border-black/10"
                  )}
                >
                  <SnapIcon className="w-4 h-4" />
                </span>
                <div>
                  <div className={cn("text-[10px] uppercase tracking-[0.3em]", isDark ? "text-white/55" : "text-black/55")}>
                    Snapchat
                  </div>
                  <div className="font-display font-medium tracking-tight">bramsgraphic78</div>
                </div>
              </a>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className={cn(
                "p-8 md:p-12 rounded-3xl",
                isDark
                  ? "bg-white/[0.025] border border-white/10 soft-shadow-dark"
                  : "bg-white border border-black/[0.06] soft-shadow-light"
              )}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    className={cn(
                      "text-[10px] uppercase tracking-[0.3em] font-medium",
                      isDark ? "text-white/55" : "text-black/55"
                    )}
                  >
                    Nom
                  </label>
                  <input
                    data-testid="contact-input-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Votre nom"
                    className={inputCls}
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label
                    className={cn(
                      "text-[10px] uppercase tracking-[0.3em] font-medium",
                      isDark ? "text-white/55" : "text-black/55"
                    )}
                  >
                    Email
                  </label>
                  <input
                    data-testid="contact-input-email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="vous@studio.com"
                    className={inputCls}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label
                  className={cn(
                    "text-[10px] uppercase tracking-[0.3em] font-medium",
                    isDark ? "text-white/55" : "text-black/55"
                  )}
                >
                  Sujet
                </label>
                <input
                  data-testid="contact-input-subject"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  placeholder="Identité de marque, refonte, direction artistique…"
                  className={inputCls}
                />
              </div>

              <div className="mt-6">
                <label
                  className={cn(
                    "text-[10px] uppercase tracking-[0.3em] font-medium",
                    isDark ? "text-white/55" : "text-black/55"
                  )}
                >
                  Message
                </label>
                <textarea
                  data-testid="contact-input-message"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Parlez-moi de votre projet, votre équipe, et vos délais."
                  rows={5}
                  className={cn(inputCls, "resize-none pt-3")}
                />
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className={cn("text-xs font-body", isDark ? "text-white/50" : "text-black/50")}>
                  En envoyant, vous acceptez d&apos;être contacté par email à propos de votre demande.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-sm transition-all duration-300 ease-in-out hover:scale-[1.02] active:scale-95 disabled:opacity-60",
                    isDark
                      ? "bg-white text-black hover:bg-white/90"
                      : "bg-black text-white hover:bg-black/90"
                  )}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={16} /> Envoi…
                    </>
                  ) : (
                    <>
                      Envoyer le message <Send size={14} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
